// packages/frontend/src/features/recipes/useServingsScale.ts
//
// Display-only servings scaler for the recipe detail view (Plan 13 Task 12).
// Pure local UI state — NO persistence, NO server call. `factor` is derived
// from `target / baseServings` and is consumed downstream by ingredient qty
// formatting (this task) and by the send-to-shopping-list flow (Task 13).
import { useState } from "react";

export interface UseServingsScaleResult {
  /** Currently selected serving count, or null when there's no base to scale from. */
  target: number | null;
  /** Multiplier to apply to ingredient quantities. 1 whenever base/target is unset. */
  factor: number;
  /** Sets the target serving count, clamped to an integer >= 1. */
  setTarget: (n: number) => void;
  /** Restores target to baseServings (factor back to 1). */
  reset: () => void;
}

const MIN_SERVINGS = 1;

/**
 * @param baseServings The recipe's stored `servings` value (number | null).
 */
export function useServingsScale(baseServings: number | null): UseServingsScaleResult {
  const [target, setTargetState] = useState<number | null>(baseServings);

  const factor =
    baseServings != null && baseServings > 0 && target != null
      ? target / baseServings
      : 1;

  function setTarget(n: number): void {
    setTargetState(Math.max(MIN_SERVINGS, Math.round(n)));
  }

  function reset(): void {
    setTargetState(baseServings);
  }

  return { target, factor, setTarget, reset };
}
